import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { supabase } from "../utils/supabaseClient";
import ProductCard from "../components/ProductCard";
import Pagination from "../components/Pagination";
import CountryToggle from "../components/CountryToggle";
import { useCountry } from "../context/CountryContext";
import { applyCountryFilter } from "../utils/country";
import { useSearch } from "../context/SearchContext";

const PAGE_SIZE = 12;

const SORTS = {
  newest: { label: "Newest first", column: "created_at", ascending: false },
  price_asc: { label: "Price: low to high", column: "price", ascending: true },
  price_desc: { label: "Price: high to low", column: "price", ascending: false },
  name: { label: "Name: A to Z", column: "name", ascending: true },
};

// % and _ act as wildcards in ILIKE, so escape them in user input
const escapeLike = (s) => s.replace(/[\\%_]/g, "\\$&");

// Values inside .or(...) must be double-quoted so commas and brackets in them are safe
const quoteFilterValue = (s) => `"${s.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;

// The navbar dropdown links use slugs such as /products/dry-fish
const CATEGORY_TITLES = {
  grains: "Grains & Flours",
  legumes: "Legumes",
  spices: "Spices & Seasonings",
  "dry-fish": "Dry Fish & Seafood",
};

const titleFromSlug = (slug) =>
  CATEGORY_TITLES[slug.toLowerCase()] ??
  slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function Products({ title: titleOverride }) {
  const { category } = useParams();
  const { searchTerm, setSearchTerm } = useSearch();
  const { country } = useCountry();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1);
  // Old links like /products/12 are product ids, not categories
  const isProductId = /^\d+$/.test(category ?? "");
  const sortParam = searchParams.get("sort");
  const sortKey = SORTS[sortParam] ? sortParam : "newest";

  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [debouncedTerm, setDebouncedTerm] = useState((searchTerm || "").trim());

  const title = titleOverride ?? (category ? titleFromSlug(category) : "All Products");

  function setParam(key, value, options) {
    const next = new URLSearchParams(searchParams);
    if (value === null || value === undefined || value === "") next.delete(key);
    else next.set(key, String(value));
    setSearchParams(next, options);
  }

  // Wait for the customer to stop typing before querying
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedTerm((searchTerm || "").trim()), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // A new category or search starts again from page 1
  const filterKey = `${category ?? ""}|${debouncedTerm}|${country}`;
  const previousFilterKey = useRef(filterKey);
  useEffect(() => {
    if (previousFilterKey.current !== filterKey) {
      previousFilterKey.current = filterKey;
      setParam("page", null, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  // Load the current page from the database
  useEffect(() => {
    if (isProductId) return undefined;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");

      const sort = SORTS[sortKey];
      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      let query = supabase.from("products").select("*", { count: "exact" });
      query = applyCountryFilter(query, country);

      // Case-insensitive "starts with": "dry-fish" matches "Dry Fish & Seafood",
      // "spices" matches both "spices" and "Spices & Seasoning"
      if (category) {
        query = query.ilike("category", `${escapeLike(category.replace(/-/g, " "))}%`);
      }
      // Every word typed must appear in the name, category or description
      if (debouncedTerm) {
        for (const word of debouncedTerm.split(/\s+/).slice(0, 5)) {
          const pattern = quoteFilterValue(`%${escapeLike(word)}%`);
          query = query.or(
            `name.ilike.${pattern},category.ilike.${pattern},description.ilike.${pattern}`
          );
        }
      }

      query = query
        .order(sort.column, { ascending: sort.ascending })
        .order("id", { ascending: true })
        .range(from, to);

      const { data, count, error: queryError } = await query;
      if (cancelled) return;

      if (queryError) {
        // Page number is beyond the last page: go back to the first one
        if (queryError.code === "PGRST103" && page > 1) {
          setParam("page", null, { replace: true });
          return;
        }
        console.error("Loading products failed:", queryError);
        setError("We couldn't load the products. Please try again.");
        setProducts([]);
        setTotal(0);
      } else {
        setProducts(data ?? []);
        setTotal(count ?? 0);
      }
      setLoading(false);
    }

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, isProductId, country, debouncedTerm, sortKey, page, reloadKey]);

  // Back to the top when the page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page]);

  if (isProductId) {
    return <Navigate to={`/product/${category}`} replace />;
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const firstShown = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const lastShown = Math.min(page * PAGE_SIZE, total);

  function handleSortChange(e) {
    const next = new URLSearchParams(searchParams);
    if (e.target.value === "newest") next.delete("sort");
    else next.set("sort", e.target.value);
    next.delete("page");
    setSearchParams(next);
  }

  let subtitle = "";
  if (!loading && !error && total > 0) {
    subtitle = `Showing ${firstShown}-${lastShown} of ${total} ${total === 1 ? "product" : "products"}`;
    if (debouncedTerm) subtitle += ` matching "${debouncedTerm}"`;
  }

  const showSkeleton = loading && products.length === 0;

  return (
    <div className="shop">
      <div className="shop-head">
        <div>
          <h2 className="shop-title">{title}</h2>
          <p className="shop-sub" aria-live="polite">
            {subtitle}
            {searchTerm && (
              <button type="button" className="shop-clear" onClick={() => setSearchTerm("")}>
                Clear search
              </button>
            )}
          </p>
        </div>

        <div className="shop-sort">
          <CountryToggle compact />
          <label className="ui-label" htmlFor="product-sort">Sort by</label>
          <select
            id="product-sort"
            className="ui-select"
            value={sortKey}
            onChange={handleSortChange}
          >
            {Object.entries(SORTS).map(([key, option]) => (
              <option key={key} value={key}>{option.label}</option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="shop-empty">
          <h3>Something went wrong</h3>
          <p>{error}</p>
          <button type="button" className="ui-btn ui-btn--primary" onClick={() => setReloadKey((k) => k + 1)}>
            Try again
          </button>
        </div>
      )}

      {!error && showSkeleton && (
        <div className="product-grid" aria-hidden="true">
          {Array.from({ length: 8 }).map((_, i) => (
            <div className="pt-card pt-skeleton" key={i}>
              <div className="pt-media" />
              <div className="pt-body">
                <div className="pt-line pt-line--short" />
                <div className="pt-line" />
                <div className="pt-line pt-line--short" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!error && !loading && products.length === 0 && (
        <div className="shop-empty">
          <h3>{debouncedTerm ? "No products found" : "Nothing here yet"}</h3>
          <p>
            {debouncedTerm
              ? `Nothing matches "${debouncedTerm}". Check the spelling or try a shorter word.`
              : country === "Nigeria"
                ? "We're still adding products for Nigeria. Check back soon, or switch to the UK flag to see our UK range."
                : "There are no products in this category at the moment."}
          </p>
          {debouncedTerm ? (
            <button type="button" className="ui-btn ui-btn--primary" onClick={() => setSearchTerm("")}>
              Clear search
            </button>
          ) : (
            <Link to="/products" className="ui-btn ui-btn--primary">
              Browse all products
            </Link>
          )}
        </div>
      )}

      {!error && products.length > 0 && (
        <>
          <div className={`product-grid${loading ? " is-loading" : ""}`}>
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          <Pagination
            page={page}
            totalPages={totalPages}
            onChange={(p) => setParam("page", p === 1 ? null : p)}
          />
        </>
      )}
    </div>
  );
}